import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, ArrowUpRight, Landmark, TrendingUp, Users,
  Play, ChevronRight, Sparkles, Quote, X, CheckCircle2, Loader2,
  Share2, MessageCircle, Facebook, Twitter, Link2, Copy, MapPin, Clock,
  Vote, RefreshCw, CheckCircle
} from 'lucide-react';
import UserLocationWidget from '../components/common/UserLocationWidget';

const HomePage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const [currentWord, setCurrentWord] = useState(0);
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);
  
  // 4e République supporter states
  const [supporterCount, setSupporterCount] = useState(1247);
  const [recentSupporters, setRecentSupporters] = useState([]);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signForm, setSignForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    city: '',
    country: "Côte d'Ivoire",
    newsletter: false
  });
  const [signStatus, setSignStatus] = useState('idle'); // idle, loading, success, error
  const [signError, setSignError] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Share URLs and messages
  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/constitution` : '';
  const shareMessages = {
    fr: `Je soutiens la 4ᵉ République pour la Côte d'Ivoire ! 🇨🇮 Rejoignez les ${supporterCount.toLocaleString()} citoyens qui veulent une nouvelle architecture institutionnelle.`,
    en: `I support the 4th Republic for Côte d'Ivoire! 🇨🇮 Join the ${supporterCount.toLocaleString()} citizens who want a new institutional architecture.`,
    pt: `Eu apoio a 4ª República para a Costa do Marfim! 🇨🇮 Junte-se aos ${supporterCount.toLocaleString()} cidadãos que querem uma nova arquitetura institucional.`
  };
  const shareMessage = shareMessages[language] || shareMessages.fr;

  const handleShare = (platform) => {
    const encodedMessage = encodeURIComponent(shareMessage);
    const encodedUrl = encodeURIComponent(shareUrl);
    
    const shareLinks = {
      whatsapp: `https://wa.me/?text=${encodedMessage}%20${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedMessage}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodedMessage}&url=${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
    };
    
    if (shareLinks[platform]) {
      window.open(shareLinks[platform], '_blank', 'width=600,height=400');
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareMessage} ${shareUrl}`);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy');
    }
  };

  // Fetch supporter count on mount
  useEffect(() => {
    const fetchSupporterCount = async () => {
      try {
        const response = await fetch(`${API_URL}/api/supporters/count`);
        if (response.ok) {
          const data = await response.json();
          setSupporterCount(data.count);
          setRecentSupporters(data.recent_supporters || []);
        }
      } catch (error) {
        console.log('Could not fetch supporter count');
      }
    };
    fetchSupporterCount();
  }, [API_URL]);

  // Handle sign form submission
  const handleSignSubmit = async (e) => {
    e.preventDefault();
    setSignStatus('loading');
    setSignError('');

    try {
      const response = await fetch(`${API_URL}/api/supporters/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signForm)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.new_count) {
          setSupporterCount(data.new_count);
        }
        setSignStatus('success');
        // Don't auto-close - let user share first
      } else {
        throw new Error('Failed to sign');
      }
    } catch (error) {
      setSignStatus('error');
      setSignError(language === 'fr' ? 'Une erreur est survenue. Veuillez réessayer.' : 'An error occurred. Please try again.');
    }
  };

  const words = {
    fr: ['Refonder', 'Libérer', 'Construire', 'Unir'],
    en: ['Rebuild', 'Free', 'Build', 'Unite'],
    pt: ['Refundar', 'Libertar', 'Construir', 'Unir']
  };

  const currentWords = words[language] || words.fr;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWord((prev) => (prev + 1) % currentWords.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [currentWords.length]);

  // Content translations
  const content = {
    fr: {
      badge: 'Parti politique autorisé depuis 2024',
      heroLine1: 'Une Nation.',
      heroLine2: 'Un avenir.',
      subtitle: 'De plateforme de réflexion à parti politique. Constructive. Contributive. Une vision qui propose des solutions concrètes.',
      ctaPrimary: 'Découvrir nos propositions',
      ctaSecondary: 'Notre posture',
      stats: {
        proposals: 'Propositions concrètes',
        axes: 'Axes majeurs',
        regions: 'Régions'
      },
      program: 'Notre programme',
      transformations: '3 Transformations',
      programSubtitle: 'Un programme concret pour une Côte d\'Ivoire souveraine et prospère.',
      propositionsCount: 'propositions',
      explore: 'Explorer',
      viewAll: 'Voir les 15 propositions',
      quote: 'La souveraineté ne se décrète plus depuis le sommet. Elle se construit, territoire par territoire.',
      joinUs: 'Rejoignez-nous',
      joinTitle: 'Construisez l\'avenir avec nous',
      joinSubtitle: 'Rejoignez le mouvement ADA. Ensemble, transformons la Côte d\'Ivoire.',
      joinCta: 'Devenir contributeur',
      governance: 'Notre gouvernance'
    },
    en: {
      badge: 'Authorized political party since 2024',
      heroLine1: 'One Nation.',
      heroLine2: 'One future.',
      subtitle: 'From think tank to political party. Constructive. Contributive. A vision that proposes concrete solutions.',
      ctaPrimary: 'Discover our proposals',
      ctaSecondary: 'Our posture',
      stats: {
        proposals: 'Concrete proposals',
        axes: 'Major axes',
        regions: 'Regions'
      },
      program: 'Our program',
      transformations: '3 Transformations',
      programSubtitle: 'A concrete program for a sovereign and prosperous Côte d\'Ivoire.',
      propositionsCount: 'proposals',
      explore: 'Explore',
      viewAll: 'View all 15 proposals',
      quote: 'Sovereignty is no longer decreed from the top. It is built, territory by territory.',
      joinUs: 'Join us',
      joinTitle: 'Build the future with us',
      joinSubtitle: 'Join the ADA movement. Together, let\'s transform Côte d\'Ivoire.',
      joinCta: 'Become a contributor',
      governance: 'Our governance'
    },
    pt: {
      badge: 'Partido político autorizado desde 2024',
      heroLine1: 'Uma Nação.',
      heroLine2: 'Um futuro.',
      subtitle: 'De plataforma de reflexão a partido político. Construtiva. Contributiva. Uma visão que propõe soluções concretas.',
      ctaPrimary: 'Descobrir as nossas propostas',
      ctaSecondary: 'A nossa postura',
      stats: {
        proposals: 'Propostas concretas',
        axes: 'Eixos principais',
        regions: 'Regiões'
      },
      program: 'O nosso programa',
      transformations: '3 Transformações',
      programSubtitle: 'Um programa concreto para uma Costa do Marfim soberana e próspera.',
      propositionsCount: 'propostas',
      explore: 'Explorar',
      viewAll: 'Ver as 15 propostas',
      quote: 'A soberania já não se decreta a partir do topo. Constrói-se, território por território.',
      joinUs: 'Junte-se a nós',
      joinTitle: 'Construa o futuro connosco',
      joinSubtitle: 'Junte-se ao movimento ADA. Juntos, transformemos a Costa do Marfim.',
      joinCta: 'Tornar-se contribuidor',
      governance: 'A nossa governança'
    }
  };

  const t = content[language] || content.fr;

  const transformations = [
    {
      id: 'institutional',
      icon: Landmark,
      title: language === 'fr' ? 'Transformation Institutionnelle' : language === 'pt' ? 'Transformação Institucional' : 'Institutional Transformation',
      subtitle: language === 'fr' ? 'Refonder l\'État' : language === 'pt' ? 'Refundar o Estado' : 'Rebuild the State',
      description: language === 'fr' 
        ? 'Provinces autonomes, Sénat territorial, Panel citoyen constitutionnel.'
        : language === 'pt'
        ? 'Províncias autónomas, Senado territorial, Painel cidadão constitucional.'
        : 'Autonomous provinces, Territorial Senate, Constitutional citizen panel.',
      color: '#5B2D8E',
      gradient: 'from-[#5B2D8E] to-[#3E1A66]',
      count: 5
    },
    {
      id: 'economic',
      icon: TrendingUp,
      title: language === 'fr' ? 'Transformation Économique' : language === 'pt' ? 'Transformação Económica' : 'Economic Transformation',
      subtitle: language === 'fr' ? 'Libérer les territoires' : language === 'pt' ? 'Libertar os territórios' : 'Free the territories',
      description: language === 'fr'
        ? '1000 PME fournisseurs, Choc logistique agricole, Souveraineté monétaire.'
        : language === 'pt'
        ? '1000 PME fornecedores, Choque logístico agrícola, Soberania monetária.'
        : '1000 supplier SMEs, Agricultural logistics shock, Monetary sovereignty.',
      color: '#F7C948',
      gradient: 'from-[#F7C948] to-[#E5A700]',
      count: 5
    },
    {
      id: 'social',
      icon: Users,
      title: language === 'fr' ? 'Transformation Sociale' : language === 'pt' ? 'Transformação Social' : 'Social Transformation',
      subtitle: language === 'fr' ? 'Servir les citoyens' : language === 'pt' ? 'Servir os cidadãos' : 'Serve citizens',
      description: language === 'fr'
        ? 'Calendrier scolaire adapté, Service civique territorial, Contrat École-Parents.'
        : language === 'pt'
        ? 'Calendário escolar adaptado, Serviço cívico territorial, Contrato Escola-Pais.'
        : 'Adapted school calendar, Territorial civic service, School-Parents contract.',
      color: '#0D9488',
      gradient: 'from-[#0D9488] to-[#065f53]',
      count: 5
    }
  ];

  const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.3 }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
  };

  return (
    <div className={`overflow-hidden ${isDark ? 'bg-[#1A0F2E]' : 'bg-[#F9FAFB]'}`}>
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          HERO SECTION - Immersive Split Layout
      ═══════════════════════════════════════════════════════════════════════════ */}
      <motion.section 
        style={{ opacity: heroOpacity, scale: heroScale }}
        className="relative min-h-screen flex items-start lg:items-center pt-24 lg:pt-0"
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 overflow-hidden">
          <div className={`absolute top-0 right-0 w-[60%] h-full ${
            isDark 
              ? 'bg-gradient-to-l from-[#5B2D8E]/20 to-transparent' 
              : 'bg-gradient-to-l from-[#5B2D8E]/5 to-transparent'
          }`} />
          <div className={`absolute bottom-0 left-0 w-[40%] h-[60%] ${
            isDark 
              ? 'bg-gradient-to-tr from-[#E5A700]/10 to-transparent' 
              : 'bg-gradient-to-tr from-[#E5A700]/5 to-transparent'
          }`} />
          {/* Decorative circles - Purple */}
          <div className={`absolute top-20 right-20 w-96 h-96 rounded-full ${
            isDark ? 'bg-[#5B2D8E]/10' : 'bg-[#5B2D8E]/5'
          } blur-3xl`} />
          {/* Decorative circles - Gold */}
          <div className={`absolute bottom-20 left-10 w-72 h-72 rounded-full ${
            isDark ? 'bg-[#E5A700]/10' : 'bg-[#E5A700]/5'
          } blur-3xl`} />
          {/* Decorative circles - Turquoise */}
          <div className={`absolute top-1/2 left-1/3 w-80 h-80 rounded-full ${
            isDark ? 'bg-[#0D9488]/15' : 'bg-[#0D9488]/5'
          } blur-3xl`} />
        </div>

        <div className="container-custom relative z-10 pt-44 sm:pt-36 lg:pt-32 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            
            {/* Left - Typography */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="show"
            >
              {/* Overline */}
              <motion.div variants={staggerItem} className="mb-6">
                <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-bold tracking-widest uppercase ${
                  isDark 
                    ? 'bg-[#F7C948]/20 text-[#F7C948]' 
                    : 'bg-[#5B2D8E]/10 text-[#5B2D8E]'
                }`}>
                  <Sparkles className="w-3.5 h-3.5" />
                  {t.badge}
                </span>
              </motion.div>

              {/* Main Headline */}
              <motion.h1 
                variants={staggerItem}
                className="mb-6"
              >
                <span className={`block text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.05] ${
                  isDark ? 'text-white' : 'text-[#0A0510]'
                }`} style={{ fontFamily: 'var(--font-heading)' }}>
                  {t.heroLine1}
                </span>
                <span className={`block text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.05] ${
                  isDark ? 'text-white' : 'text-[#0A0510]'
                }`} style={{ fontFamily: 'var(--font-heading)' }}>
                  {t.heroLine2}
                </span>
                <span className="block text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.05]" style={{ fontFamily: 'var(--font-heading)' }}>
                  <span className={`bg-gradient-to-r ${isDark ? 'from-[#F7C948] to-[#FFD966]' : 'from-[#5B2D8E] to-[#7A45B0]'} bg-clip-text text-transparent`}>
                    {currentWords[currentWord]}
                  </span>
                  <span className={isDark ? 'text-white' : 'text-[#0A0510]'}>.</span>
                </span>
              </motion.h1>

              {/* Subheadline */}
              <motion.p 
                variants={staggerItem}
                className={`text-lg sm:text-xl leading-relaxed mb-10 max-w-xl ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}
                style={{ fontFamily: 'var(--font-body)' }}
              >
                {t.subtitle}
              </motion.p>

              {/* CTA Buttons */}
              <motion.div variants={staggerItem} className="flex flex-wrap gap-4">
                <Link
                  to="/propositions"
                  className={`group inline-flex items-center gap-3 px-8 py-4 rounded-sm font-bold text-base transition-all duration-300 hover:-translate-y-1 ${
                    isDark 
                      ? 'bg-[#F7C948] text-[#0A0510] hover:bg-[#FFD966]' 
                      : 'bg-[#F7C948] text-[#0A0510] hover:bg-[#E5A700]'
                  }`}
                  style={{ boxShadow: '0 10px 40px rgba(247, 201, 72, 0.3)' }}
                  data-testid="hero-cta-primary"
                >
                  {t.ctaPrimary}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                
                <Link
                  to="/notre-posture"
                  className={`group inline-flex items-center gap-3 px-8 py-4 rounded-sm font-bold text-base border-2 transition-all duration-300 hover:-translate-y-1 ${
                    isDark 
                      ? 'border-white/20 text-white hover:bg-white/10 hover:border-white/30' 
                      : 'border-[#0A0510]/20 text-[#0A0510] hover:bg-[#1A0F2E]/5 hover:border-[#0A0510]/30'
                  }`}
                  data-testid="hero-cta-secondary"
                >
                  {t.ctaSecondary}
                  <Play className="w-4 h-4" />
                </Link>
              </motion.div>
            </motion.div>

            {/* Right - Visual Element */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              {/* Stats Cards */}
              <div className="grid grid-cols-2 gap-4">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className={`col-span-2 p-8 rounded-sm ${
                    isDark 
                      ? 'bg-gradient-to-br from-[#5B2D8E] to-[#3E1A66]' 
                      : 'bg-gradient-to-br from-[#5B2D8E] to-[#7A45B0]'
                  }`}
                >
                  <div className="text-6xl font-black text-white mb-2" style={{ fontFamily: 'var(--font-heading)' }}>15</div>
                  <div className="text-white/80 font-medium">{t.stats.proposals}</div>
                </motion.div>
                
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className={`p-6 rounded-sm ${
                    isDark ? 'bg-[#150A20] border border-white/10' : 'bg-white border border-black/5 shadow-xl'
                  }`}
                >
                  <div className={`text-4xl font-black mb-1 ${isDark ? 'text-[#F7C948]' : 'text-[#5B2D8E]'}`} style={{ fontFamily: 'var(--font-heading)' }}>3</div>
                  <div className="text-theme-muted text-sm font-medium">{t.stats.axes}</div>
                </motion.div>
                
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className={`p-6 rounded-sm ${
                    isDark ? 'bg-[#150A20] border border-white/10' : 'bg-white border border-black/5 shadow-xl'
                  }`}
                >
                  <div className={`text-4xl font-black mb-1 ${isDark ? 'text-[#0D9488]' : 'text-[#0D9488]'}`} style={{ fontFamily: 'var(--font-heading)' }}>31</div>
                  <div className="text-theme-muted text-sm font-medium">{t.stats.regions}</div>
                </motion.div>
              </div>

            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer"
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          whileHover={{ scale: 1.1 }}
        >
          <div className={`w-8 h-12 rounded-full border-2 ${isDark ? 'border-white/30 hover:border-white/50' : 'border-black/20 hover:border-black/40'} flex justify-center pt-3 transition-colors`}>
            <motion.div 
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className={`w-1.5 h-3 rounded-full ${isDark ? 'bg-[#E5A700]' : 'bg-[#5B2D8E]'}`}
            />
          </div>
          <motion.span 
            className={`block text-[10px] mt-2 uppercase tracking-widest text-center ${isDark ? 'text-white/40' : 'text-black/40'}`}
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            Scroll
          </motion.span>
        </motion.div>
      </motion.section>

      {/* ═══════════════════════════════════════════════════════════════════════════
          USER LOCATION SECTION - Personalized experience
      ═══════════════════════════════════════════════════════════════════════════ */}
      <section className={`py-8 ${isDark ? 'bg-[#1A0F2E]' : 'bg-[#F5F0E8]'}`}>
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <UserLocationWidget variant="full" />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════════
          4ᵉ RÉPUBLIQUE SECTION - Prominent Call to Action with Supporter Counter
      ═══════════════════════════════════════════════════════════════════════════ */}
      <section className={`py-16 lg:py-20 ${isDark ? 'bg-[#150A20]' : 'bg-[#5B2D8E]'}`}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-sm"
          >
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16 p-8 lg:p-12">
              {/* Left - Icon & Badge */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="w-24 h-24 lg:w-32 lg:h-32 rounded-sm bg-gradient-to-br from-[#F7C948] to-[#E5A700] flex items-center justify-center shadow-2xl" style={{ boxShadow: '0 20px 60px rgba(247, 201, 72, 0.4)' }}>
                    <span className="text-4xl lg:text-5xl font-black text-[#5B2D8E]" style={{ fontFamily: 'var(--font-heading)' }}>4ᵉ</span>
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-[#0D9488] rounded-sm flex items-center justify-center">
                    <Landmark className="w-5 h-5 text-white" />
                  </div>
                </div>
              </div>

              {/* Center - Content */}
              <div className="flex-1 text-center lg:text-left">
                <span className="inline-block px-4 py-2 rounded-sm text-xs font-bold tracking-[0.2em] uppercase bg-white/20 text-white mb-4">
                  {language === 'fr' ? 'Projet Constitutionnel' : language === 'en' ? 'Constitutional Project' : 'Projeto Constitucional'}
                </span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  {language === 'fr' ? 'La 4ᵉ République' : language === 'en' ? 'The 4th Republic' : 'A 4ª República'}
                </h2>
                <p className="text-lg text-white/80 mb-6 max-w-2xl" style={{ fontFamily: 'var(--font-body)' }}>
                  {language === 'fr' 
                    ? 'Une nouvelle architecture institutionnelle pour la Côte d\'Ivoire. Provinces autonomes, Sénat territorial, Panel citoyen constitutionnel.'
                    : language === 'en'
                    ? 'A new institutional architecture for Côte d\'Ivoire. Autonomous provinces, Territorial Senate, Constitutional citizen panel.'
                    : 'Uma nova arquitetura institucional para a Costa do Marfim. Províncias autónomas, Senado territorial, Painel cidadão constitucional.'}
                </p>
                
                {/* Supporter Counter - Social Proof */}
                <div className="mb-6">
                  <motion.div 
                    initial={{ scale: 0.9, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    className="inline-flex items-center gap-3 px-5 py-3 rounded-sm bg-white/10 backdrop-blur-sm border border-white/20"
                  >
                    <div className="flex -space-x-2">
                      {[...Array(4)].map((_, i) => (
                        <div 
                          key={i} 
                          className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F7C948] to-[#0D9488] border-2 border-[#5B2D8E] flex items-center justify-center text-xs font-bold text-[#5B2D8E]"
                        >
                          {recentSupporters[i]?.first_name?.[0] || ['A', 'K', 'M', 'S'][i]}
                        </div>
                      ))}
                    </div>
                    <div>
                      <motion.span 
                        key={supporterCount}
                        initial={{ y: 10, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        className="text-2xl font-black text-[#F7C948]" 
                        style={{ fontFamily: 'var(--font-heading)' }}
                      >
                        {supporterCount.toLocaleString()}
                      </motion.span>
                      <span className="block text-xs text-white/70">
                        {language === 'fr' ? 'citoyens soutiennent' : language === 'en' ? 'citizens support' : 'cidadãos apoiam'}
                      </span>
                    </div>
                  </motion.div>
                </div>

                {/* Articles Phares - Key Points */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {/* Article 47 */}
                  <div className="p-4 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 hover:border-[#F7C948]/50 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <Vote className="w-5 h-5 text-[#F7C948]" />
                      <span className="text-xs font-bold text-[#F7C948]">Art. 47</span>
                    </div>
                    <h4 className="text-sm font-bold text-white mb-1">
                      {language === 'fr' ? 'Tout électeur né ivoirien peut être candidat' : language === 'en' ? 'Any Ivorian-born voter can be a candidate' : 'Todo eleitor nascido marfinense pode ser candidato'}
                    </h4>
                    <p className="text-xs text-white/60">
                      {language === 'fr' ? 'Parrainages citoyens sur 3 mois' : language === 'en' ? 'Citizen sponsorships over 3 months' : 'Patrocínios cidadãos em 3 meses'}
                    </p>
                  </div>

                  {/* Article 55 */}
                  <div className="p-4 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 hover:border-[#F7C948]/50 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <RefreshCw className="w-5 h-5 text-[#F7C948]" />
                      <span className="text-xs font-bold text-[#F7C948]">Art. 55</span>
                    </div>
                    <h4 className="text-sm font-bold text-white mb-1">
                      {language === 'fr' ? 'Mandats sans limitation' : language === 'en' ? 'Unlimited terms' : 'Mandatos sem limitação'}
                    </h4>
                    <p className="text-xs text-white/60">
                      {language === 'fr' ? 'Confiance renouvelable par le Peuple' : language === 'en' ? 'Renewable trust by the People' : 'Confiança renovável pelo Povo'}
                    </p>
                  </div>

                  {/* Article 56 */}
                  <div className="p-4 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 hover:border-[#F7C948]/50 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-5 h-5 text-[#F7C948]" />
                      <span className="text-xs font-bold text-[#F7C948]">Art. 56</span>
                    </div>
                    <h4 className="text-sm font-bold text-white mb-1">
                      {language === 'fr' ? 'Le Peuple seul décide' : language === 'en' ? 'The People alone decide' : 'Só o Povo decide'}
                    </h4>
                    <p className="text-xs text-white/60">
                      {language === 'fr' ? 'Souveraineté populaire absolue' : language === 'en' ? 'Absolute popular sovereignty' : 'Soberania popular absoluta'}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                  <button
                    onClick={() => setShowSignModal(true)}
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-[#F7C948] text-[#0A0510] font-bold rounded-sm hover:bg-[#FFD966] transition-all duration-300 hover:-translate-y-1"
                    style={{ boxShadow: '0 10px 40px rgba(247, 201, 72, 0.3)' }}
                    data-testid="4e-republique-sign-cta"
                  >
                    {language === 'fr' ? 'Je soutiens ce projet' : language === 'en' ? 'I support this project' : 'Eu apoio este projeto'}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <Link
                    to="/constitution"
                    className="inline-flex items-center gap-3 px-8 py-4 border-2 border-white/30 text-white font-bold rounded-sm hover:bg-white/10 hover:border-white/50 transition-all duration-300"
                    data-testid="4e-republique-discover"
                  >
                    {language === 'fr' ? 'Découvrir le projet' : language === 'en' ? 'Discover the project' : 'Descobrir o projeto'}
                  </Link>
                </div>
              </div>

              {/* Right - Stats */}
              <div className="hidden xl:flex flex-col gap-4">
                <div className="text-center p-4 rounded-sm bg-white/10 backdrop-blur-sm">
                  <div className="text-3xl font-black text-[#F7C948]" style={{ fontFamily: 'var(--font-heading)' }}>31</div>
                  <div className="text-xs text-white/70 font-medium uppercase tracking-wider">
                    {language === 'fr' ? 'Provinces' : language === 'en' ? 'Provinces' : 'Províncias'}
                  </div>
                </div>
                <div className="text-center p-4 rounded-sm bg-white/10 backdrop-blur-sm">
                  <div className="text-3xl font-black text-[#0D9488]" style={{ fontFamily: 'var(--font-heading)' }}>5</div>
                  <div className="text-xs text-white/70 font-medium uppercase tracking-wider">
                    {language === 'fr' ? 'Réformes' : language === 'en' ? 'Reforms' : 'Reformas'}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════════
          SIGN SUPPORT MODAL
      ═══════════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showSignModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => signStatus !== 'loading' && setShowSignModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className={`relative w-full max-w-lg rounded-sm overflow-hidden ${
                isDark ? 'bg-[#1A0F2E]' : 'bg-white'
              }`}
              style={{ boxShadow: '0 25px 100px rgba(0,0,0,0.4)' }}
            >
              {/* Header */}
              <div className="relative px-6 py-8 bg-gradient-to-br from-[#5B2D8E] to-[#3E1A66]">
                <button
                  onClick={() => signStatus !== 'loading' && setShowSignModal(false)}
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                  data-testid="close-sign-modal"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-sm bg-[#F7C948] flex items-center justify-center">
                    <span className="text-2xl font-black text-[#5B2D8E]">4ᵉ</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                      {language === 'fr' ? 'Je soutiens la 4ᵉ République' : language === 'en' ? 'I support the 4th Republic' : 'Eu apoio a 4ª República'}
                    </h3>
                    <p className="text-white/70 text-sm">
                      {language === 'fr' ? 'Rejoignez les ' : language === 'en' ? 'Join the ' : 'Junte-se aos '}
                      <span className="text-[#F7C948] font-bold">{supporterCount.toLocaleString()}</span>
                      {language === 'fr' ? ' citoyens' : language === 'en' ? ' citizens' : ' cidadãos'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Form / Success */}
              <div className="p-6">
                {signStatus === 'success' ? (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-center py-6"
                  >
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#0D9488]/20 flex items-center justify-center">
                      <CheckCircle2 className="w-10 h-10 text-[#0D9488]" />
                    </div>
                    <h4 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {language === 'fr' ? 'Merci pour votre soutien !' : language === 'en' ? 'Thank you for your support!' : 'Obrigado pelo seu apoio!'}
                    </h4>
                    <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {language === 'fr' 
                        ? 'Votre voix compte. Ensemble, construisons la 4ᵉ République.'
                        : language === 'en'
                        ? 'Your voice matters. Together, let\'s build the 4th Republic.'
                        : 'A sua voz importa. Juntos, vamos construir a 4ª República.'}
                    </p>

                    {/* Share Section */}
                    <div className={`pt-6 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-center gap-2 mb-4">
                        <Share2 className={`w-4 h-4 ${isDark ? 'text-[#F7C948]' : 'text-[#5B2D8E]'}`} />
                        <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {language === 'fr' ? 'Partagez et multipliez l\'impact !' : language === 'en' ? 'Share and multiply the impact!' : 'Partilhe e multiplique o impacto!'}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-center gap-3">
                        {/* WhatsApp - Primary for Africa */}
                        <button
                          onClick={() => handleShare('whatsapp')}
                          className="flex items-center gap-2 px-5 py-3 bg-[#25D366] text-white font-bold rounded-sm hover:bg-[#20bd5a] transition-all hover:-translate-y-1"
                          data-testid="share-whatsapp"
                        >
                          <MessageCircle className="w-5 h-5" />
                          WhatsApp
                        </button>
                        
                        {/* Facebook */}
                        <button
                          onClick={() => handleShare('facebook')}
                          className="p-3 bg-[#1877F2] text-white rounded-sm hover:bg-[#166fe5] transition-all hover:-translate-y-1"
                          data-testid="share-facebook"
                        >
                          <Facebook className="w-5 h-5" />
                        </button>
                        
                        {/* Twitter/X */}
                        <button
                          onClick={() => handleShare('twitter')}
                          className={`p-3 rounded-sm transition-all hover:-translate-y-1 ${
                            isDark ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'
                          }`}
                          data-testid="share-twitter"
                        >
                          <Twitter className="w-5 h-5" />
                        </button>
                        
                        {/* Copy Link */}
                        <button
                          onClick={handleCopyLink}
                          className={`p-3 rounded-sm transition-all hover:-translate-y-1 ${
                            linkCopied 
                              ? 'bg-[#0D9488] text-white' 
                              : isDark 
                                ? 'bg-white/10 text-white hover:bg-white/20' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          data-testid="share-copy"
                        >
                          {linkCopied ? <CheckCircle2 className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                        </button>
                      </div>
                      
                      {linkCopied && (
                        <motion.p 
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-[#0D9488] text-sm mt-3 font-medium"
                        >
                          {language === 'fr' ? 'Lien copié !' : language === 'en' ? 'Link copied!' : 'Link copiado!'}
                        </motion.p>
                      )}
                      
                      {/* Close button */}
                      <button
                        onClick={() => {
                          setShowSignModal(false);
                          setSignStatus('idle');
                          setSignForm({
                            first_name: '',
                            last_name: '',
                            email: '',
                            city: '',
                            country: "Côte d'Ivoire",
                            newsletter: false
                          });
                        }}
                        className={`mt-6 text-sm font-medium ${isDark ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
                        data-testid="close-after-share"
                      >
                        {language === 'fr' ? 'Fermer' : language === 'en' ? 'Close' : 'Fechar'}
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSignSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                          {language === 'fr' ? 'Prénom' : language === 'en' ? 'First name' : 'Nome'}*
                        </label>
                        <input
                          type="text"
                          required
                          value={signForm.first_name}
                          onChange={(e) => setSignForm({...signForm, first_name: e.target.value})}
                          className={`w-full px-4 py-3 rounded-sm border transition-colors ${
                            isDark 
                              ? 'bg-white/5 border-white/10 text-white focus:border-[#F7C948]' 
                              : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-[#5B2D8E]'
                          } outline-none`}
                          placeholder="Kouadio"
                          data-testid="sign-firstname"
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                          {language === 'fr' ? 'Nom' : language === 'en' ? 'Last name' : 'Apelido'}*
                        </label>
                        <input
                          type="text"
                          required
                          value={signForm.last_name}
                          onChange={(e) => setSignForm({...signForm, last_name: e.target.value})}
                          className={`w-full px-4 py-3 rounded-sm border transition-colors ${
                            isDark 
                              ? 'bg-white/5 border-white/10 text-white focus:border-[#F7C948]' 
                              : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-[#5B2D8E]'
                          } outline-none`}
                          placeholder="Amani"
                          data-testid="sign-lastname"
                        />
                      </div>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                        Email*
                      </label>
                      <input
                        type="email"
                        required
                        value={signForm.email}
                        onChange={(e) => setSignForm({...signForm, email: e.target.value})}
                        className={`w-full px-4 py-3 rounded-sm border transition-colors ${
                          isDark 
                            ? 'bg-white/5 border-white/10 text-white focus:border-[#F7C948]' 
                            : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-[#5B2D8E]'
                        } outline-none`}
                        placeholder="kouadio@exemple.ci"
                        data-testid="sign-email"
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                        {language === 'fr' ? 'Ville' : language === 'en' ? 'City' : 'Cidade'}
                      </label>
                      <input
                        type="text"
                        value={signForm.city}
                        onChange={(e) => setSignForm({...signForm, city: e.target.value})}
                        className={`w-full px-4 py-3 rounded-sm border transition-colors ${
                          isDark 
                            ? 'bg-white/5 border-white/10 text-white focus:border-[#F7C948]' 
                            : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-[#5B2D8E]'
                        } outline-none`}
                        placeholder="Abidjan"
                        data-testid="sign-city"
                      />
                    </div>

                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="newsletter"
                        checked={signForm.newsletter}
                        onChange={(e) => setSignForm({...signForm, newsletter: e.target.checked})}
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-[#5B2D8E] focus:ring-[#5B2D8E]"
                        data-testid="sign-newsletter"
                      />
                      <label htmlFor="newsletter" className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                        {language === 'fr' 
                          ? 'Je souhaite recevoir les actualités du mouvement ADA'
                          : language === 'en'
                          ? 'I want to receive news from the ADA movement'
                          : 'Quero receber notícias do movimento ADA'}
                      </label>
                    </div>

                    {signError && (
                      <p className="text-red-500 text-sm">{signError}</p>
                    )}

                    <button
                      type="submit"
                      disabled={signStatus === 'loading'}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#F7C948] text-[#0A0510] font-bold rounded-sm hover:bg-[#E5A700] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="sign-submit"
                    >
                      {signStatus === 'loading' ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {language === 'fr' ? 'Signature en cours...' : language === 'en' ? 'Signing...' : 'Assinando...'}
                        </>
                      ) : (
                        <>
                          {language === 'fr' ? 'Signer mon soutien' : language === 'en' ? 'Sign my support' : 'Assinar meu apoio'}
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>

                    <p className={`text-xs text-center ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
                      {language === 'fr' 
                        ? 'Vos données sont protégées et ne seront jamais partagées.'
                        : language === 'en'
                        ? 'Your data is protected and will never be shared.'
                        : 'Os seus dados estão protegidos e nunca serão partilhados.'}
                    </p>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════════════
          TRANSFORMATIONS SECTION - Bento Grid
      ═══════════════════════════════════════════════════════════════════════════ */}
      <section className={`py-24 lg:py-32 ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'}`}>
        <div className="container-custom">
          {/* Section Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className={`inline-block px-4 py-2 rounded-sm text-xs font-bold tracking-[0.2em] uppercase mb-6 ${
              isDark ? 'bg-white/5 text-[#F7C948]' : 'bg-[#5B2D8E]/5 text-[#5B2D8E]'
            }`}>
              {t.program}
            </span>
            <h2 className={`text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-6 ${isDark ? 'text-white' : 'text-[#0A0510]'}`} style={{ fontFamily: 'var(--font-heading)' }}>
              {t.transformations}
            </h2>
            <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontFamily: 'var(--font-body)' }}>
              {t.programSubtitle}
            </p>
          </motion.div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {transformations.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                >
                  <Link
                    to={`/propositions#${item.id}`}
                    className={`group relative block h-full p-8 rounded-sm border overflow-hidden transition-all duration-500 ${
                      isDark 
                        ? 'bg-[#150A20] border-white/5 hover:border-white/20' 
                        : 'bg-[#FAFAFA] border-black/5 hover:border-black/10 hover:shadow-2xl'
                    }`}
                    data-testid={`transformation-${item.id}`}
                  >
                    {/* Hover gradient overlay */}
                    <div 
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{
                        background: `linear-gradient(135deg, ${item.color}10 0%, transparent 60%)`
                      }}
                    />
                    
                    <div className="relative z-10">
                      {/* Icon */}
                      <div 
                        className={`w-14 h-14 rounded-sm bg-gradient-to-br ${item.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300`}
                        style={{
                          boxShadow: `0 10px 30px ${item.color}30`
                        }}
                      >
                        <Icon className="w-7 h-7 text-white" />
                      </div>

                      {/* Count Badge */}
                      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-sm text-xs font-bold mb-4 ${
                        isDark ? 'bg-white/10 text-white/70' : 'bg-black/5 text-black/50'
                      }`}>
                        {item.count} {t.propositionsCount}
                      </div>

                      {/* Title */}
                      <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-[#0A0510]'}`} style={{ fontFamily: 'var(--font-heading)' }}>
                        {item.title}
                      </h3>
                      
                      {/* Subtitle */}
                      <p className="font-semibold mb-4" style={{ color: item.color }}>
                        {item.subtitle}
                      </p>

                      {/* Description */}
                      <p className={`text-sm leading-relaxed mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} style={{ fontFamily: 'var(--font-body)' }}>
                        {item.description}
                      </p>

                      {/* CTA */}
                      <div className={`flex items-center gap-2 text-sm font-bold transition-all group-hover:gap-3 ${
                        isDark ? 'text-[#F7C948]' : 'text-[#5B2D8E]'
                      }`}>
                        {t.explore}
                        <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {/* View All CTA */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/propositions"
              className={`inline-flex items-center gap-3 px-8 py-4 rounded-sm font-bold transition-all duration-300 hover:-translate-y-1 ${
                isDark 
                  ? 'bg-white/10 text-white hover:bg-white/20' 
                  : 'bg-[#5B2D8E]/10 text-[#5B2D8E] hover:bg-[#5B2D8E]/20'
              }`}
              data-testid="view-all-propositions"
            >
              {t.viewAll}
              <ChevronRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════════
          QUOTE SECTION - Full Width Impact with Turquoise accent
      ═══════════════════════════════════════════════════════════════════════════ */}
      <section className={`py-24 lg:py-32 relative overflow-hidden ${isDark ? 'bg-gradient-to-br from-[#1A0F2E] via-[#150A20] to-[#1A0F2E]' : 'bg-gradient-to-br from-[#5B2D8E] to-[#3D1E5F]'}`}>
        {/* Turquoise accent glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#0D9488]/10 rounded-full blur-[100px]" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <Quote className="w-12 h-12 mx-auto mb-8 text-[#0D9488]/70" />
            <blockquote className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              {t.quote}
            </blockquote>
            <div className="flex items-center justify-center gap-4">
              <div className="w-12 h-0.5 bg-[#0D9488]" />
              <span className="text-white/70 font-bold tracking-widest uppercase text-sm">ADA</span>
              <div className="w-12 h-0.5 bg-[#0D9488]" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════════
          CTA SECTION - Join the Movement
      ═══════════════════════════════════════════════════════════════════════════ */}
      <section className={`py-24 lg:py-32 ${isDark ? 'bg-[#1A0F2E]' : 'bg-[#FAFAFA]'}`}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`relative overflow-hidden rounded-sm p-12 lg:p-20 bg-gradient-to-br from-[#F7C948] to-[#E5A700]`}
          >
            {/* Background Pattern with turquoise accent */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#0D9488] rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 max-w-3xl">
              <span className="inline-block px-4 py-2 rounded-sm text-xs font-bold tracking-[0.2em] uppercase bg-[#5B2D8E] text-white mb-6">
                {t.joinUs}
              </span>
              
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#0A0510] tracking-tight mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                {t.joinTitle}
              </h2>
              
              <p className="text-lg text-[#0A0510]/70 mb-10 max-w-xl" style={{ fontFamily: 'var(--font-body)' }}>
                {t.joinSubtitle}
              </p>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/rejoindre"
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-[#5B2D8E] text-white font-bold rounded-sm hover:bg-[#4A2472] transition-all duration-300 hover:-translate-y-1"
                  data-testid="cta-join"
                >
                  {t.joinCta}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                
                <Link
                  to="/gouvernance"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-[#0D9488] text-white font-bold rounded-sm hover:bg-[#0b7c72] transition-all duration-300 hover:-translate-y-1"
                  data-testid="cta-governance"
                >
                  {t.governance}
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
